import React, { useMemo, useState } from 'react';

function wilsonCI(ns, total) {
  if (total === 0) return { lo: 0, hi: 0, halfWidth: 0 };
  const z = 1.96; // 95% confidence
  const p = ns / total;
  const denominator = 1 + (z * z) / total;
  const centerAdjustment = p + (z * z) / (2 * total);
  const spread = z * Math.sqrt((p * (1 - p) + (z * z) / (4 * total)) / total);
  const lo = Math.max(0, (centerAdjustment - spread) / denominator);
  const hi = Math.min(1, (centerAdjustment + spread) / denominator);
  return { lo, hi, halfWidth: (hi - lo) / 2 };
}

export function ChampionProbBar({ result, resultNoAbsences }) {
  const [mode, setMode] = useState('with'); // 'with' | 'without'
  const active = mode === 'without' && resultNoAbsences ? resultNoAbsences : result;

  const rows = useMemo(() => {
    return active.teams
      .map((team, i) => {
        const count = active.stageCounts.champion[i];
        const ci = wilsonCI(count, active.numSimulations);
        return {
          team,
          idx: i,
          pct: count / active.numSimulations,
          ci,
          avgGF: active.totalGoalsFor[i] / active.numSimulations,
          avgGA: active.totalGoalsAgainst[i] / active.numSimulations,
        };
      })
      .filter((r) => r.pct > 0)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 16);
  }, [active]);

  const max = rows[0]?.pct ?? 1;

  return (
    <div className="card-elev p-5 sm:p-6 mb-8 border border-[#2A4A6B]">
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-heading text-2xl font-bold text-white tracking-tight">
            Probabilités de Victoire Finale
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Basé sur {active.numSimulations.toLocaleString()} simulations de la phase finale.
          </p>
        </div>
        {resultNoAbsences && (
          <div className="inline-flex items-center gap-1 rounded-full border border-[#2A4A6B] bg-[#1E3A5F]/20 p-1 text-xs">
            <button
              onClick={() => setMode('with')}
              className={`rounded-full px-3 py-1 font-mono uppercase text-[10px] tracking-wider transition-colors ${
                mode === 'with'
                  ? 'bg-red-500/10 text-red-400 font-bold'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Avec blessures
            </button>
            <button
              onClick={() => setMode('without')}
              className={`rounded-full px-3 py-1 font-mono uppercase text-[10px] tracking-wider transition-colors ${
                mode === 'without'
                  ? 'bg-emerald-500/10 text-emerald-400 font-bold'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sans blessures
            </button>
          </div>
        )}
      </header>

      <div className="space-y-3">
        {rows.map((r, i) => {
          const isWinner = i === 0;
          return (
            <div
              key={r.team.id}
              className={`relative grid grid-cols-[30px_1fr_60px] sm:grid-cols-[40px_140px_1fr_80px] items-center gap-2 rounded-lg border p-3 ${
                isWinner
                  ? 'border-[#00C853]/40 bg-gradient-to-r from-[#00C853]/5 to-transparent'
                  : 'border-[#2A4A6B]/50 bg-[#1E3A5F]/10 hover:bg-[#1E3A5F]/20 transition-colors'
              }`}
            >
              {/* Rank */}
              <span className="font-mono text-xs text-gray-500 text-center font-bold">
                #{i + 1}
              </span>

              {/* Team Flag & Name */}
              <div className="flex items-center gap-2 min-w-0">
                <img 
                  src={r.team.flag ? (r.team.flag.startsWith('http') ? r.team.flag : `https://flagcdn.com/w80/${r.team.flag.toLowerCase()}.png`) : `https://flagcdn.com/w80/un.png`} 
                  alt="" 
                  className="w-6 h-4 object-cover rounded shadow-sm shrink-0" 
                />
                <span className="truncate font-semibold text-white text-xs sm:text-sm">
                  {r.team.name_fr || r.team.name_en || r.team.name}
                </span>
              </div>

              {/* Probability Bar */}
              <div className="relative col-span-3 sm:col-span-1 h-3 rounded-full bg-[#1E3A5F]/60 overflow-hidden mt-1 sm:mt-0">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${(r.pct / max) * 100}%`,
                    background: isWinner
                      ? 'linear-gradient(90deg, #00C853 0%, #05f267 100%)'
                      : 'linear-gradient(90deg, #0288d1 0%, #00b0ff 100%)',
                    boxShadow: isWinner ? '0 0 10px rgba(0, 200, 83, 0.4)' : 'none',
                  }}
                />
              </div>

              {/* Percentages */}
              <div className="text-right font-mono text-xs tabular-nums text-white">
                <div className="font-bold">{(r.pct * 100).toFixed(1)}%</div>
                <div className="text-[9px] text-gray-500">
                  ±{(r.ci.halfWidth * 100).toFixed(2)}%
                </div>
              </div>

              {/* Floating stats info inside match details on hover (visible only on hover in CSS) */}
              <div className="hidden absolute left-48 bottom-full z-20 mb-2 p-2.5 rounded bg-[#1E3A5F] border border-[#2A4A6B] text-[10px] text-gray-300 font-mono shadow-xl space-y-0.5 group-hover:block">
                <div>ELO: {r.team.elo}</div>
                <div>Buts marqués: {r.avgGF.toFixed(1)}</div>
                <div>Buts encaissés: {r.avgGA.toFixed(1)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
